// frontend/packages/common/api/Widgets/TableReservation/Reservations.js
import {useState} from 'react';
import axiosWidgetInstance, {withApiKey, widgetEndpoints} from 'packages/common/utils/axiosWidget';
import {widgetLogger} from 'packages/common/utils/widgetLogger';
import useSWR from 'swr';

/**
 * Hook pour récupérer la configuration de réservation via l'API externe
 * @param {string} companyId - ID de l'établissement
 * @param {string} apiKey - Clé API pour l'authentification
 * @return {Object} - Données de configuration, états de chargement et erreurs
 */
export const useExternalReservationConfig = (companyId, apiKey) => {
  const endpointUrl = companyId ? widgetEndpoints.externalApi.reservation.config(companyId) : null;

  // Fetcher function for SWR - simplifiée et sans logs verbeux
  const fetcher = async ([url, key]) => {
    try {
      const response = await axiosWidgetInstance.get(url, withApiKey(key));
      return response.data;
    } catch (error) {
      // Les logs d'erreur sont gérés par l'intercepteur axios
      throw error;
    }
  };

  // Configuration SWR pour réduire les retry et le spam de logs
  const {data, error, mutate} = useSWR(
    endpointUrl ? [endpointUrl, apiKey] : null,
    fetcher,
    {
      // Réduire les retry pour éviter le spam de logs
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      // Désactiver la revalidation automatique pour les widgets
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return {
    config: data?.data,
    isLoading: endpointUrl !== null && !error && !data,
    isError: !!error,
    error,
    mutate,
  };
};

/**
 * Hook pour récupérer les dates disponibles via l'API externe
 * @return {Object} - Fonction de récupération et états
 */
export const useExternalAvailableDates = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAvailableDates = async (companyId, params, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (!params.service || !params.partySize) {
        throw new Error('service et partySize sont requis');
      }

      queryParams.append('service', params.service);
      queryParams.append('partySize', params.partySize);

      if (params.year) queryParams.append('year', params.year);
      if (params.month) queryParams.append('month', params.month);

      const queryString = `?${queryParams.toString()}`;
      const url = `${widgetEndpoints.externalApi.reservation.dates(companyId)}${queryString}`;

      const response = await axiosWidgetInstance.get(url, withApiKey(apiKey));
      return response.data?.data?.dates || [];
    } catch (apiError) {
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {getAvailableDates, loading, error};
};

/**
 * Hook pour récupérer les créneaux horaires disponibles via l'API externe
 * @return {Object} - Fonction de récupération et états
 */
export const useExternalTimeSlots = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getTimeSlotsForDate = async (companyId, params, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (!params.date || !params.service || !params.partySize) {
        throw new Error('date, service et partySize sont requis');
      }

      queryParams.append('date', params.date);
      queryParams.append('service', params.service);
      queryParams.append('partySize', params.partySize);

      const queryString = `?${queryParams.toString()}`;
      const url = `${widgetEndpoints.externalApi.reservation.timeslots(companyId)}${queryString}`;

      const response = await axiosWidgetInstance.get(url, withApiKey(apiKey));
      return response.data?.data?.timeslots || [];
    } catch (apiError) {
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {getTimeSlotsForDate, loading, error};
};

/**
 * Hook pour créer une réservation via l'API externe
 * @return {Object} - Fonction de création et états
 */
export const useExternalCreateReservation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createReservation = async (companyId, data, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      // Prepare reservation data
      const reservationData = {
        ...data,
        dateTime: data.dateTime ? new Date(data.dateTime).toISOString() : undefined,
        numberOfGuests: parseInt(data.numberOfGuests, 10) || 1,
        duration: data.duration ? parseInt(data.duration, 10) : undefined,
      };

      // Log uniquement les données importantes en debug
      widgetLogger.debug('Creating reservation', {
        companyId,
        dateTime: reservationData.dateTime,
        numberOfGuests: reservationData.numberOfGuests,
      });

      const response = await axiosWidgetInstance.post(
          widgetEndpoints.externalApi.reservation.create(companyId),
          reservationData,
          withApiKey(apiKey),
      );

      widgetLogger.info('Reservation created successfully');
      return response.data;
    } catch (apiError) {
      setError(apiError);
      widgetLogger.error('Failed to create reservation', apiError.message);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {createReservation, loading, error};
};
